import placeholderImg from "@assets/images/placeholder255x255.png";
import Button from "@material-ui/core/Button";
import DialogContentText from "@material-ui/core/DialogContentText";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import ActionDialog from "@saleor/components/ActionDialog";
import AssignInstagramDialog from "@saleor/components/AssignInstagramDialog/AssignInstagramDialog";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import RecommendProductDialog from "@saleor/components/RecommendProductDialog";
import { RecommendProductTab } from "@saleor/components/RecommendProductSection";
import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useOnSetDefaultVariant from "@saleor/hooks/useOnSetDefaultVariant";
// import { createPaginationState } from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import {
  useInstagramCopyFromProductsMutation,
  useInstagramVisibleProductInsMutation
} from "@saleor/instagram/mutations";
import { instagramUrl } from "@saleor/instagram/urls";
import { commonMessages } from "@saleor/intl";
import AssignVariantDialogSetPrice from "@saleor/products/components/AssignVariantDialogSetPrice";
import {
  useClearImagesByProductIdMutation,
  useProductDeleteMutation,
  useProductImageCreateMutation,
  useProductImageDeleteMutation,
  useProductImagesReorder,
  useProductImageVisibleEditByUrlMutation,
  useProductInsReorderMutation,
  useProductNotTopInsReorderMutation,
  useProductS3DetailpageImageFetchMutation,
  useProductS3ImageFetchMutation,
  useProductSetAvailabilityForPurchase,
  useProductSortProductInsMutation,
  useproductUnsortProductInsMutation,
  useProductUpdateMutation,
  useProductVariantBulkDeleteMutation,
  useProductVariantBulkPublisheMutation,
  useProductVariantReorderMutation,
  useSimpleProductUpdateMutation,
  useVariantBulkSetPriceMutation
} from "@saleor/products/mutations";
import { useUnassignRecommendProductMutation } from "@saleor/products/mutations";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useProductSearch from "@saleor/searches/useProductSearch";
import useProductVariantSearch from "@saleor/searches/useProductVariantSearch";
import { ReorderEvent } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createMetadataUpdateHandler from "@saleor/utils/handlers/metadataUpdateHandler";
import { move } from "@saleor/utils/lists";
import { mapEdgesToItems } from "@saleor/utils/maps";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import { getMutationState, maybe } from "../../../misc";
import ProductUpdatePage from "../../components/ProductUpdatePage";
import { useProductDetails } from "../../queries";
import { ProductImageCreateVariables } from "../../types/ProductImageCreate";
import { ProductUpdate as ProductUpdateMutationResult } from "../../types/ProductUpdate";
import {
  productImageUrl,
  productListUrl,
  productUrl,
  ProductUrlDialog,
  ProductUrlQueryParams,
  productVariantAddUrl,
  productVariantCreatorUrl,
  productVariantEditUrl
} from "../../urls";
import {
  RecommendationTargetTypeEnum,
  RecommendationTypeEnum
} from "./../../../types/globalTypes";
import {
  createImageReorderHandler,
  createImageUploadHandler,
  createUpdateHandler,
  createVariantReorderHandler
} from "./handlers";

interface ProductUpdateProps {
  id: string;
  params: ProductUrlQueryParams;
}

export function getActiveTab(tabName: string): RecommendProductTab {
  return tabName === RecommendProductTab.also_likes
    ? RecommendProductTab.also_likes
    : RecommendProductTab.recommends;
}

export const ProductUpdate: React.FC<ProductUpdateProps> = ({ id, params }) => {
  const location = useLocation();
  const isBuytogether = location.pathname.includes("products");
  const navigate = useNavigator();
  const notify = useNotifier();
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const intl = useIntl();
  const {
    loadMore: loadMoreCategories,
    search: searchCategories,
    result: searchCategoriesOpts
  } = useCategorySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreCollections,
    search: searchCollections,
    result: searchCollectionsOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    search: searchProducts,
    loadMore: loadMoreProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: {
      after: null,
      first: 100,
      query: ""
    }
  });
  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });
  const shop = useShop();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});

  const { data, loading, refetch } = useProductDetails({
    displayLoader: true,
    variables: {
      id,
      isOrder: "true"
    }
  });

  const handleUpdate = (data: ProductUpdateMutationResult) => {
    if (data.productUpdate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  };
  const [updateProduct, updateProductOpts] = useProductUpdateMutation({
    onCompleted: handleUpdate
  });
  const [
    updateSimpleProduct,
    updateSimpleProductOpts
  ] = useSimpleProductUpdateMutation({
    onCompleted: handleUpdate
  });

  const [
    reorderProductImages,
    reorderProductImagesOpts
  ] = useProductImagesReorder({});

  const [deleteProduct, deleteProductOpts] = useProductDeleteMutation({
    onCompleted: data => {
      if (data.productDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Product removed"
          })
        });
        navigate(productListUrl());
      } else {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Product has been sold/added and cannot be deleted."
          })
        });
        closeModal();
      }
    }
  });

  const [
    createProductImage,
    createProductImageOpts
  ] = useProductImageCreateMutation({
    onCompleted: data => {
      const imageError = data.productImageCreate.errors.find(
        error => error.field === ("image" as keyof ProductImageCreateVariables)
      );
      if (imageError) {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
    }
  });

  const [deleteProductImage] = useProductImageDeleteMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });

  const [
    productS3ImageFetch,
    productS3ImageFetchOpts
  ] = useProductS3ImageFetchMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });
  const [
    productS3DetailpageImageFetch,
    productS3DetailpageImageFetchOpts
  ] = useProductS3DetailpageImageFetchMutation({
    onCompleted: () =>
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      })
  });
  const [
    clearImagesByProductId,
    clearImagesByProductIdOpts
  ] = useClearImagesByProductIdMutation({
    onCompleted: data => {
      if (data?.clearImagesByProductId?.errors?.length === 0) {
        closeModal();
        reset();
        refetch();
      }
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  });
  const [
    productUnsortProductins,
    productUnsortProductinsOpts
  ] = useproductUnsortProductInsMutation({
    onCompleted: data => {
      if (data?.productUnsortProductins?.errors?.length === 0) {
        closeModal();
        reset();
        refetch();
      }
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges)
      });
    }
  });

  const [
    bulkProductVariantDelete,
    bulkProductVariantDeleteOpts
  ] = useProductVariantBulkDeleteMutation({
    onCompleted: data => {
      if (data.productVariantBulkDelete.errors.length === 0) {
        closeModal();
        reset();
        refetch();
      }
    }
  });
  const [productBulkPublish] = useProductVariantBulkPublisheMutation({
    onCompleted: data => {
      if (data.productVariantBulkPublishe.productErrors.length === 0) {
        closeModal();
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        reset();
        refetch();
      }
    }
  });

  const [
    setProductAvailability,
    productAvailabilityOpts
  ] = useProductSetAvailabilityForPurchase({
    onCompleted: data => {
      const errors = data?.productSetAvailabilityForPurchase?.errors;
      if (errors?.length === 0) {
        const updatedProduct = data?.productSetAvailabilityForPurchase?.product;
        setProduct(product => ({
          ...product,
          availableForPurchase: updatedProduct.availableForPurchase,
          isAvailableForPurchase: updatedProduct.isAvailableForPurchase,
          preOrder: updatedProduct.preOrder,
          isPreOrder: updatedProduct.isPreOrder
        }));
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Product availability updated",
            description: "snackbar text"
          })
        });
      }
    }
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ProductUrlDialog,
    ProductUrlQueryParams
  >(navigate, params => productUrl(id, params), params);

  const handleBack = () => navigate(productListUrl());

  const [product, setProduct] = useStateFromProps(data?.product);

  if (product === null) {
    return <NotFoundPage onBack={handleBack} />;
  }

  const handleVariantAdd = () => navigate(productVariantAddUrl(id));

  const handleImageDelete = (id: string) => () =>
    deleteProductImage({ variables: { id } });
  const handleImageEdit = (imageId: string) => () =>
    navigate(productImageUrl(id, imageId));
  const handleSubmit = createMetadataUpdateHandler(
    product,
    createUpdateHandler(
      product,
      variables => updateProduct({ variables }),
      variables => updateSimpleProduct({ variables }),
      variables => setProductAvailability({ variables })
    ),
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables })
  );
  const handleImageUpload = createImageUploadHandler(id, variables =>
    createProductImage({ variables })
  );

  const handleImageFetch = (id: string) => () =>
    productS3ImageFetch({ variables: { productId: id } });

  const handleDetailpageImageFetch = (id: string) => () =>
    productS3DetailpageImageFetch({ variables: { productId: id } });

  const handleImageReorder = createImageReorderHandler(product, variables =>
    reorderProductImages({ variables })
  );

  const [
    reorderProductVariants,
    reorderProductVariantsOpts
  ] = useProductVariantReorderMutation({});

  const handleVariantReorder = createVariantReorderHandler(product, variables =>
    reorderProductVariants({ variables })
  );

  const disableFormSave =
    createProductImageOpts.loading ||
    deleteProductOpts.loading ||
    reorderProductImagesOpts.loading ||
    updateProductOpts.loading ||
    productAvailabilityOpts.loading ||
    productS3ImageFetchOpts.loading ||
    productS3DetailpageImageFetchOpts.loading ||
    reorderProductVariantsOpts.loading ||
    loading;

  const formTransitionState = getMutationState(
    updateProductOpts.called || updateSimpleProductOpts.called,
    updateProductOpts.loading || updateSimpleProductOpts.loading,
    maybe(() => updateProductOpts.data.productUpdate.errors),
    maybe(() => updateSimpleProductOpts.data.productUpdate.errors),
    maybe(() => updateSimpleProductOpts.data.productVariantUpdate.errors)
  );

  const categories = maybe(
    () => searchCategoriesOpts.data.search.edges,
    []
  ).map(edge => edge.node);
  const collections = maybe(
    () => searchCollectionsOpts.data.search.edges,
    []
  ).map(edge => edge.node);
  const errors = [
    ...maybe(() => updateProductOpts.data.productUpdate.errors, []),
    ...maybe(() => updateSimpleProductOpts.data.productUpdate.errors, [])
  ];
  const onSetDefaultVariant = useOnSetDefaultVariant(
    product ? product.id : null,
    null
  );

  const [productInsReorder] = useProductInsReorderMutation({
    onCompleted: data => {
      if (data.productReorderInstagram.errors.length !== 0) {
        notify({
          status: "error",
          text: "error"
        });
      }
    }
  });
  const [productNotTopInsReorder] = useProductNotTopInsReorderMutation({
    onCompleted: data => {
      if (data.productReorderNottopInstagram.errors.length !== 0) {
        notify({
          status: "error",
          text: "error"
        });
      }
    }
  });
  const handleInsReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    productInsReorder({
      optimisticResponse: {
        productReorderInstagram: {
          __typename: "ProductReorderInstagram",
          product: {
            ...data.product,
            productIns: move(
              data.product.productSortIns[oldIndex],
              data.product.productSortIns,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        id,
        move: {
          id: data.product.productSortIns[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });

  const handleNotTopInsReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    productNotTopInsReorder({
      optimisticResponse: {
        productReorderNottopInstagram: {
          __typename: "ProductReorderNotTopInstagram",
          product: {
            ...data.product,
            productIns: move(
              data.product.productIns[oldIndex],
              data.product.productIns,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        id,
        move: {
          id: data.product.productIns[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });

  const [assignProductIns] = useProductSortProductInsMutation({
    onCompleted: data => {
      if (data.productSortProductins.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added Content to Product"
          })
        });
        navigate(productUrl(id), true);
      }
    }
  });

  const [visibleProductIns] = useInstagramVisibleProductInsMutation({
    onCompleted: data => {
      if (data.instagramVisibleProductIns.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Changed Content Visible"
          })
        });
        navigate(productUrl(id), true);
      }
    }
  });


  const [visibleProductNoTopIns] = useInstagramVisibleProductInsMutation({
    onCompleted: data => {
      if (data.instagramVisibleProductIns.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Changed Content Visible"
          })
        });

      }
    }
  });

  const [onInsSelectReorder] = useProductInsReorderMutation({
    onCompleted: data => {
      if (data.productReorderInstagram.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Reorder Content"
          })
        });
        navigate(productUrl(id), true);
      }
    }
  });

  const handleInsSelectReorder = (insId: string, sortOrder: number) => {
    onInsSelectReorder({
      variables: {
        id,
        move: {
          id: insId,
          sortOrder
        }
      }
    });
  };

  const [unassignProduct] = useUnassignRecommendProductMutation({
    onCompleted: data => {
      if (data.recommendProductRemove.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Deleted recommend product from product."
          })
        });
        reset();
        // closeModal();
      }
    }
  });

  // const changeTab = (tabName: RecommendProductTab) => {
  //   reset();
  //   navigate(
  //     productUrl(id, {
  //       activeTab: tabName
  //     })
  //   );
  // };
  const [
    copyProductsIns,
    copyProductsInsOpts
  ] = useInstagramCopyFromProductsMutation({
    onCompleted: data => {
      if (data.instagramCopyFromProducts.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Copy contents from product."
          })
        });
        reset();
      }
    }
  });
  const [
    setVariantsPrice,
    setVariantsPriceOpts
  ] = useVariantBulkSetPriceMutation({
    onCompleted: data => {
      if (data.variantBulkSetPrice.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Variants Bulk Set Price."
          })
        });
        reset();
      }
    }
  });
  const [imageVisibleEditByUrl] = useProductImageVisibleEditByUrlMutation({
    onCompleted: data => {
      if (data.productImageVisibleEditByUrl.productErrors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Image Visible Edit Success"
          })
        });
        refetch();
      }
    }
  });
  const {
    loadMore: loadMoreProductVariants,
    search: searchProductVariants,
    result: searchProductVariantsOpts
  } = useProductVariantSearch({
    variables: {
      productId: data?.product?.id,
      ...DEFAULT_INITIAL_SEARCH_DATA
    }
  });
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");



  const [onNotTopInsSelectReorder] = useProductNotTopInsReorderMutation({
    onCompleted: data => {
      if (data.productReorderNottopInstagram.errors.length === 0) {

        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Reorder Content"
          })
        });
       
      }
    }
  });

  const handleNotTopInsSelectReorder = (insId: string, sortOrder: number) => {
    onNotTopInsSelectReorder({
      variables: {
        id,
        move: {
          id: insId,
          sortOrder
        }
      }
    });
  };


  return (
    <>
      <WindowTitle title={maybe(() => data.product.name)} />
      <ProductUpdatePage
        isBuytogether={isBuytogether}
        // changeTab={changeTab}
        // currentTab={params.activeTab}
        onVisibleEdit={id =>
          imageVisibleEditByUrl({
            variables: {
              id
            }
          })
        }
        categories={categories}
        collections={collections}
        defaultWeightUnit={shop?.defaultWeightUnit}
        disabled={disableFormSave}
        onSetDefaultVariant={onSetDefaultVariant}
        errors={errors}
        fetchCategories={searchCategories}
        fetchCollections={searchCollections}
        saveButtonBarState={formTransitionState}
        images={maybe(() => data.product.imagesDas)}
        header={maybe(() => product.name)}
        placeholderImage={placeholderImg}
        product={product}
        warehouses={
          warehouses.data?.warehouses?.edges?.map(edge => edge.node) || []
        }
        taxTypes={data?.taxTypes}
        variants={maybe(() => product.variants)}
        onBack={handleBack}
        onRowClick={id => {
          navigate(instagramUrl(id));
        }}
        onDelete={() => openModal("remove")}
        onImageReorder={handleImageReorder}
        onSubmit={handleSubmit}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        onVariantAdd={handleVariantAdd}
        onVariantsAdd={() => navigate(productVariantCreatorUrl(id))}
        onImagesClean={() => openModal("clearn-images", {})}
        onPriceSet={() => openModal("set-price", {})}
        onVariantShow={variantId => () =>
          navigate(productVariantEditUrl(product.id, variantId))}
        onVariantReorder={handleVariantReorder}
        onRecommendAssign={() => openModal("assign-recommend")}
        onAlsolikeAssign={() => openModal("assign-alsolike")}
        onBuytogetherAssign={() => openModal("assign-buytogether")}
        onRecommendUnassign={(productId, event) => {
          event.stopPropagation();
          unassignProduct({
            variables: {
              targetId: id,
              targetType: RecommendationTargetTypeEnum.PRODUCT,
              recommendType: RecommendationTypeEnum.RECOMMEND,
              productIds: [productId]
            }
          });
        }}
        onAlsolikeUnassign={(productId, event) => {
          event.stopPropagation();
          unassignProduct({
            variables: {
              targetId: id,
              targetType: RecommendationTargetTypeEnum.PRODUCT,
              recommendType: RecommendationTypeEnum.ALSO_LIKE,
              productIds: [productId]
            }
          });
        }}
        onBuytogetherUnAssign={(productId, event) => {
          event.stopPropagation();
          unassignProduct({
            variables: {
              targetId: id,
              targetType: RecommendationTargetTypeEnum.PRODUCT,
              recommendType: RecommendationTypeEnum.BUY_TOGETHER,
              productIds: [productId]
            }
          });
        }}
        onImageUpload={handleImageUpload}
        onImageFetch={handleImageFetch}
        onDetailpageImageFetch={handleDetailpageImageFetch}
        onImageEdit={handleImageEdit}
        onImageDelete={handleImageDelete}
        onInsSelectReorder={handleInsSelectReorder}
        toolbar={
          <>
            <Button
              color="primary"
              onClick={() =>
                openModal("unpublish", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Unpublish"
                description="unpublish product, button"
              />
            </Button>
            <Button
              color="primary"
              onClick={() =>
                openModal("publish", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Publish"
                description="publish product, button"
              />
            </Button>
            <IconButton
              color="primary"
              onClick={() =>
                openModal("remove-variants", {
                  ids: listElements
                })
              }
            >
              <DeleteIcon />
            </IconButton>
          </>
        }
        recommendToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-recommend", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign recommend product from product, button"
            />
          </Button>
        }
        alsolikeToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-alsolike", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign alsolike product from product, button"
            />
          </Button>
        }
        buytogetherToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-buytogether", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign alsolike product from product, button"
            />
          </Button>
        }
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        fetchMoreCategories={{
          hasMore: maybe(
            () => searchCategoriesOpts.data.search.pageInfo.hasNextPage
          ),
          loading: searchCategoriesOpts.loading,
          onFetchMore: loadMoreCategories
        }}
        fetchMoreCollections={{
          hasMore: maybe(
            () => searchCollectionsOpts.data.search.pageInfo.hasNextPage
          ),
          loading: searchCollectionsOpts.loading,
          onFetchMore: loadMoreCollections
        }}
        productIns={data?.product?.productSortIns}
        onInsReorder={handleInsReorder}
        onDeleteIns={id =>
          openModal("remove-parductins", {
            id
          })
        }
        onAddIns={() => openModal("assign")}
        onCopyIns={() => openModal("copyins")}
        reverseVisible={instagrams =>
          visibleProductIns({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
      />
      <AssignInstagramDialog
        instargrams={data?.product?.productIns}
        open={params.action === "assign"}
        onClose={closeModal}
        onRowClick={id => () => navigate(instagramUrl(id))}
        onSubmit={instagrams =>
          assignProductIns({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
        reverseVisible={instagrams =>
          visibleProductNoTopIns({
            variables: {
              ids: instagrams.map(instagram => instagram.id)
            }
          })
        }
        onInsReorder={handleNotTopInsReorder}
        onInsSelectReorder={handleNotTopInsSelectReorder}
      />
      <AssignProductDialog
        confirmButtonState={copyProductsInsOpts.status}
        hasMore={searchProductsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "copyins"}
        onFetch={searchProducts}
        onFetchMore={loadMoreProducts}
        loading={searchProductsOpts.loading}
        onClose={closeModal}
        onSubmit={products => {
          copyProductsIns({
            variables: {
              id,
              ids: products.map(product => product.id)
            }
          });
        }}
        products={mapEdgesToItems(searchProductsOpts?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <ActionDialog
        open={params.action === "remove"}
        onClose={closeModal}
        confirmButtonState={deleteProductOpts.status}
        onConfirm={() => deleteProduct({ variables: { id } })}
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Product",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to delete {name}?"
            description="delete product"
            values={{
              name: product ? product.name : undefined
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "remove-variants"}
        onClose={closeModal}
        confirmButtonState={bulkProductVariantDeleteOpts.status}
        onConfirm={() =>
          bulkProductVariantDelete({
            variables: {
              ids: params.ids
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Product Variants",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to delete this variant?} other{Are you sure you want to delete {displayQuantity} variants?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "publish"}
        confirmButtonState={bulkProductVariantDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productBulkPublish({
            variables: {
              ids: params.ids,
              isPublished: true
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Publish Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to publish this product?} other{Are you sure you want to publish {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "unpublish"}
        confirmButtonState={bulkProductVariantDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          productBulkPublish({
            variables: {
              ids: params.ids,
              isPublished: false
            }
          })
        }
        title={intl.formatMessage({
          defaultMessage: "Unpublish Products",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to unpublish this product?} other{Are you sure you want to unpublish {displayQuantity} products?}}"
            description="dialog content"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "clearn-images"}
        confirmButtonState={clearImagesByProductIdOpts.status}
        title={intl.formatMessage({
          defaultMessage: "Clearn Variant Images",
          description: "dialog header"
        })}
        onConfirm={() =>
          clearImagesByProductId({
            variables: {
              productId: data?.product?.id
            }
          })
        }
        onClose={closeModal}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure to clean the product's sku images?"
            description="dialog content"
          />
        </DialogContentText>
      </ActionDialog>
      <AssignVariantDialogSetPrice
        currencySymbol={currencySymbol}
        confirmButtonState={setVariantsPriceOpts.status}
        hasMore={searchProductVariantsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "set-price"}
        onFetch={searchProductVariants}
        onFetchMore={loadMoreProductVariants}
        loading={searchProductVariantsOpts.loading}
        onClose={closeModal}
        onSubmit={(variants, price) => {
          setVariantsPrice({
            variables: {
              ids: variants.map(variant => variant.id),
              price
            }
          });
        }}
        variants={mapEdgesToItems(
          searchProductVariantsOpts?.data?.search
        )?.filter(suggestedVariant => suggestedVariant.id)}
      />
      <ActionDialog
        open={params.action === "remove-parductins"}
        onClose={closeModal}
        confirmButtonState={productUnsortProductinsOpts.status}
        onConfirm={() =>
          productUnsortProductins({
            variables: {
              id: params.id
            }
          })
        }
        variant="delete"
        title={intl.formatMessage({
          defaultMessage: "Delete Product Content",
          description: "dialog header"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="Are you sure you want to cancel the sorting of this content in this product?"
            description="dialog content"
          />
        </DialogContentText>
      </ActionDialog>
      <RecommendProductDialog
        targetId={id}
        targetType={RecommendationTargetTypeEnum.PRODUCT}
        closeModal={closeModal}
        reset={reset}
        action={params.action}
        ids={params.ids}
      />
    </>
  );
};
export default ProductUpdate;
